import { Card } from "@nimit9/signet-ui/components/card"
import { ErrorState } from "@nimit9/signet-ui/components/error-state"
import { RatioFill } from "@nimit9/signet-ui/components/privacy-mask"
import { useMediaQuery } from "@nimit9/signet-ui/hooks/use-media-query"
import type { MerchantDetailTransaction } from "@paisa/types"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { Amount } from "@/components/common/amount"
import { Header } from "@/components/layout/header"
import { PageWrapper } from "@/components/layout/page-wrapper"
import { api } from "@/lib/api"
import { CATEGORY_COLORS, PERIOD_OPTIONS } from "@/lib/constants"
import { decryptTransactionRows } from "@/lib/crypto"
import { useCryptoStore } from "@/store/crypto"
import { useUIStore } from "@/store/ui"
import { BackToAnalytics, DetailSkeleton, EntityDetailBody } from "./-entity-detail"

export const Route = createFileRoute("/analytics/merchants/$id")({
  component: MerchantDetail,
})

type MonthlyTotal = { month: string; total: number; count: number }
type RecentTxn = MerchantDetailTransaction
type CatBreakdown = {
  name: string | null
  type: string | null
  icon: string | null
  total: number
  count: number
}
type MerchantDetailData = {
  success: true
  merchant: {
    id: string
    cleanName: string
    rawId: string
    isPerson: boolean
    isRecurring: boolean
  }
  monthlyTotals: MonthlyTotal[]
  recentTransactions: RecentTxn[]
  categoryBreakdown: CatBreakdown[]
  stats: { total: number; avgMonthly: number; count: number }
}

function MerchantDetail() {
  const { id } = Route.useParams()
  const isDesktop = useMediaQuery("(min-width: 768px)")
  const period = useUIStore((s) => s.period)
  // "All" has months: 0 — map it to all history, as useAnalytics does.
  const months = PERIOD_OPTIONS.find((p) => p.label === period)?.months || 600

  const key = useCryptoStore((s) => s.key)
  const mode = useCryptoStore((s) => s.mode)
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["analytics", "merchant", id, months, mode, !!key],
    queryFn: async () => {
      const res = await api.get<MerchantDetailData>(`/analytics/merchants/${id}?months=${months}`)
      await decryptTransactionRows(res.recentTransactions, key, mode)
      return res
    },
    staleTime: 5 * 60 * 1000,
  })

  if (isLoading) {
    return (
      <>
        <Header title="Merchant" showPeriod action={<BackToAnalytics />} />
        <PageWrapper>
          <DetailSkeleton />
        </PageWrapper>
      </>
    )
  }

  if (isError || !data) {
    return (
      <>
        <Header title="Merchant" showPeriod action={<BackToAnalytics />} />
        <PageWrapper>
          <ErrorState message={(error as Error)?.message} onRetry={refetch} />
        </PageWrapper>
      </>
    )
  }

  const { merchant, monthlyTotals, recentTransactions, categoryBreakdown, stats } = data
  const maxCat = categoryBreakdown.length > 0 ? categoryBreakdown[0].total : 1

  return (
    <>
      <Header title={merchant.cleanName} showPeriod action={<BackToAnalytics />} />
      <PageWrapper>
        <EntityDetailBody
          kind="merchant"
          name={merchant.cleanName}
          stats={stats}
          monthlyTotals={monthlyTotals}
          months={months}
          color="var(--chart-3)"
          isDesktop={isDesktop}
          recent={recentTransactions.map((txn) => ({
            id: txn.id,
            date: txn.date,
            primary: (
              <>
                {txn.categoryIcon && <span className="mr-1.5">{txn.categoryIcon}</span>}
                {txn.description.slice(0, 50)}
              </>
            ),
            secondary: txn.categoryName ?? undefined,
            amount: txn.amount,
            type: txn.type,
          }))}
          breakdown={
            categoryBreakdown.length > 0 && (
              <Card className="min-w-0 gap-0 py-0" data-testid="merchant-categories">
                <h2 className="border-b border-border-subtle px-4 py-3 text-callout font-semibold text-text-primary md:px-5">
                  Category breakdown
                </h2>
                <ul className="flex flex-col gap-3 px-4 py-3 md:px-5">
                  {categoryBreakdown.map((cat) => (
                    <li key={cat.name || "uncategorized"}>
                      <div className="flex min-w-0 items-center justify-between gap-3">
                        <span className="min-w-0 truncate text-body text-text-primary">
                          {cat.icon && <span className="mr-1.5">{cat.icon}</span>}
                          {cat.name || "Uncategorized"}
                        </span>
                        <span className="flex shrink-0 items-baseline gap-2">
                          <Amount value={cat.total} className="text-body text-text-secondary" />
                          <span className="font-mono text-caption text-text-muted">
                            {cat.count}×
                          </span>
                        </span>
                      </div>
                      <div aria-hidden className="mt-1.5 h-1 rounded-full bg-bg-elevated">
                        <RatioFill
                          pct={(cat.total / maxCat) * 100}
                          className="h-full rounded-full"
                          style={{
                            backgroundColor: CATEGORY_COLORS[cat.type || ""] || "var(--text-muted)",
                          }}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              </Card>
            )
          }
        />
      </PageWrapper>
    </>
  )
}
